import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnInit,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  Company,
  ContentRecord,
  GeneratedContentDetails,
  HuntPackageRecord,
} from '@cyborg-monorepo/hunter/common';
import { SupportedTools, toolFactory } from '@cyborg-monorepo/hunter/matchboi';
import Mark from 'mark.js';
import { getToolIcon, supportedToolUrl } from '../../../../helpers/utils';
import {
  TrackingEventEnum,
  TrackingService,
} from '../../../../tracking/tracking.service';
import { SearchStateService } from '../../../research/services/search-state.service';
import { CompanyService } from '../../services/company.service';
import { ContentService } from '../../services/content.service';
import { MarkCopiedComponent } from '../cyborg-snackbar/cyborg-snackbar';
import { UpdatedHuntPackage } from '../../../hunt/hunt-template/custom-hunt-package/updated-hunt-package-interface';
import { categories } from '@cyborg-monorepo/hunter/matchboi';

@Component({ standalone: false, 
  selector: 'cyborg-content',
  templateUrl: './cyborg-content.component.html',
  styleUrls: ['./cyborg-content.component.scss'],
})
export class CyborgContentComponent implements OnInit, AfterViewInit {
  @Input() public content: Partial<ContentRecord>;
  @Input() public huntPackage:
    | Partial<HuntPackageRecord>
    | Partial<UpdatedHuntPackage>;
  public compatible: boolean;
  public contentDetails: Partial<ContentRecord>;
  public generatedContentDetails: GeneratedContentDetails;
  public generatedAddtionalContentDetails: Array<GeneratedContentDetails> = [];
  private companyDetails: Company;
  public getToolIcon = getToolIcon;
  public supportedToolUrl = supportedToolUrl;

  constructor(
    private myElement: ElementRef,
    private searchStateService: SearchStateService,
    private contentService: ContentService,
    private snackBarService: MatSnackBar,
    private trackingService: TrackingService,
    public companyService: CompanyService,
  ) {}

  public ngOnInit(): void {
    this.companyService.getCompanyDetails().subscribe((company) => {
      this.companyDetails = company;
    });
  }

  public ngAfterViewInit(): void {
    setTimeout(() => {
      this.textHightlighting();
    }, 0);
  }

  public onCopy(target: EventTarget): void {
    const text = (target as HTMLElement).parentNode as HTMLElement;
    navigator.clipboard.writeText(
      text.getElementsByTagName('pre')[0].textContent,
    );

    this.snackBarService.openFromComponent(MarkCopiedComponent, {
      duration: 5 * 1000,
    });
  }

  public openDetails(content: Partial<ContentRecord>): void {
    this.contentDetails = content;
    this.getContent(this.contentDetails);
    if (this.contentDetails?.additional_queries?.length > 0) {
      this.getAdditionalContent(this.contentDetails);
    }
    this.trackingService.trackEvent(
      TrackingEventEnum.RESEARCH_HUNT_PACKAGE_CONTENT_ACCESSED,
      {
        huntPackage: {
          UUID: this.huntPackage.UUID,
          title: this.huntPackage.title,
        },
        content: {
          title: content.title,
          UUID: content.UUID,
          goal: content.goal,
          tool: content.tool,
          platform_type: content.platform_type,
        },
      },
    );
  }

  public getContent(content: Partial<ContentRecord>): void {
    this.contentService.getContent(content).subscribe((generatedContent) => {
      this.generatedContentDetails = generatedContent;
    });
  }

  //Seperate function due to multiple additional queries
  public getAdditionalContent(content: Partial<ContentRecord>): void {
    this.contentService
      .getAdditionalContent(content)
      .subscribe((allGeneratedContent) => {
        this.generatedAddtionalContentDetails = allGeneratedContent;
      });
  }

  /**
   * Builds the Tool URI based on the contentTool selected and the content of the record
   */
  public getSearchURLForContent(
    contentTool: SupportedTools,
    content: string,
  ): string {
    return toolFactory(contentTool).generateSearchPermalink(
      this.getUserToolRootURL(contentTool),
      content,
    );
  }

  /**
   * Return readable version of default CCM category if it exists, otherwise return user's custom category
   */
  public getReadableCategory(category: string): string {
    return categories[category]?.name ? categories[category].name : category;
  }

  /**
   * Returns user's tool root URL for the given tool, or returns null if not set
   */
  public getUserToolRootURL(contentTool: SupportedTools): string | null {
    return this.companyDetails?.toolMetadata?.[contentTool]?.url;
  }

  private textHightlighting(): void {
    const context =
      this.myElement.nativeElement.querySelectorAll('p, content-title');
    const instance = new Mark(context);
    const term = this.searchStateService.state.value.term;
    if (term) {
      instance.mark(term, {
        element: 'em',
        className: 'highlight',
      });
    }
  }
}
